// Filters that come and go with the calendar: holidays, and the monthly First
// Friday. Each one appears a few weeks ahead of its day and retires the moment
// the day is over, so the drawer is never carrying a Valentine's chip in August.
//
// Computed here rather than stored in lyh_filter_groups on purpose. Half these
// days FLOAT - Thanksgiving is the fourth Thursday, Memorial Day the last
// Monday, Easter is the computus - so a stored row would need its dates rewritten
// every December by someone who remembered to. A function gets it right forever,
// and First Friday (twelve occurrences a year) can't reasonably be rows at all.
//
// These still behave exactly like a saved group everywhere else: picking one sets
// ?group=<slug>, so a Halloween link can be pasted into a Facebook thread the
// same way the Reddit-answer links are.
//
// ── Matching: keyword PROPOSES, a person CONFIRMS ────────────────────────────
// The keyword rules below never filter anything on their own. They only propose.
// What an event actually belongs to is lyh_events.occasions, written by a person
// at approval time.
//
// That split isn't caution for its own sake, it was measured. Run live against
// the real table, a keyword rule files "Cars & Coffee Lynchburg (Christmas in
// July)" under Christmas and a July history pub crawl under Halloween, because a
// themed name is not the occasion. proposeOccasions already narrows this a long
// way by asking against the EVENT'S OWN DATE - nothing in July falls inside any
// Christmas window - but the last step is still a person saying yes.
//
// The consequence worth knowing up front: an occasion nobody has confirmed
// anything for renders no filter at all. This ships inert and lights up one
// occasion at a time as events get tagged, which is the right way round to fail.
//
// None of this touches matchesGroup. The saved groups in lyh_filter_groups match
// on the event NAME alone and have to keep doing that: extending them to notes
// was measured on 2026-07-28 and moved 143 event/group pairs, turning `outdoors`
// from 24 events into 83 by matching "Parks & Rec" in the note of a Preschool
// Play Group. Those group links are sitting in public comments right now.
//
// ── Adding a day from the notable-days inbox ─────────────────────────────────
// The LiL scrapes collect days people around here care about - free ice cream
// day, a World Cup match, the day the fair opens - into the vault at
// events/cities/lynchburg/notes/notable-days.md. Each entry there carries the
// date, what the day is, a line of what people said, the capture date and a
// source permalink; only the first two matter here, the rest is provenance.
//
// Turning one into a live filter is ONE entry added to SEASONAL below, and
// nothing else. "Free Ice Cream Day, Apr 15" becomes:
//
//   {
//     slug: 'free-ice-cream-day',
//     label: 'Free ice cream day',
//     blurb: 'Scoop giveaways and cone deals around town.',
//     match: /free (?:ice )?cream|free cone|scoop/i,
//     when: { kind: 'fixed', month: 4, day: 15 },
//   }
//
// Pick the `match` off the words the events themselves use, not the words the
// day is called by. A day with no annual rhythm (a World Cup match) uses
// explicit dates instead:
//
//     when: { kind: 'dates', dates: ['2026-06-11', '2026-06-15'] }
//
// Add a `leadDays` if a month of notice is wrong for it. The chip then appears
// on its own, and stays hidden until at least one event actually matches, so a
// day nobody ends up listing anything for costs the drawer nothing.

import { addDays, endOfDay, startOfDay } from 'date-fns'
import type { EventRow, FilterGroup } from '../types'

/** How far ahead a holiday or cultural-observance chip appears. Lisa asked for
    about a month for both - "4 weeks before major holidays... as well as for
    any major cultural events (Pride Week, Black History Month)" - so the
    cultural entries below reuse this same default rather than a second
    constant. */
const HOLIDAY_LEAD_DAYS = 28
/** First Friday is monthly, so it only needs the week running up to it. */
const FIRST_FRIDAY_LEAD_DAYS = 7
/** National/food/awareness days (Donut Day, Pi Day, ...): Lisa asked for two
    weeks, half the holiday lead. There are far more of these than holidays,
    so a month of notice would mean several are always stacked up in the
    drawer at once; two weeks keeps each one feeling timely. */
const NATIONAL_DAY_LEAD_DAYS = 14

type DateSpec =
  /** Same calendar date every year. month is 1-12. */
  | { kind: 'fixed'; month: number; day: number }
  /** The nth weekday of a month: weekday 0=Sun..6=Sat, nth 1-4 or -1 for last. */
  | { kind: 'nth'; month: number; weekday: number; nth: number }
  | { kind: 'easter' }
  /** The first Friday of every month. */
  | { kind: 'firstFridayMonthly' }
  /** Explicit one-off dates, 'YYYY-MM-DD'. */
  | { kind: 'dates'; dates: string[] }
  /** A month-long (or cross-month) observance rather than a single day - Black
      History Month, Hispanic Heritage Month. startDay/endDay default to the
      1st and the last day of their month, so a leap February takes care of
      itself. leadDays still counts back from the START, same as everywhere
      else, but the chip stays live through the END rather than one day. */
  | { kind: 'monthRange'; startMonth: number; startDay?: number; endMonth: number; endDay?: number }

export interface SeasonalDef {
  /** Also the ?group= value, so it has to stay stable once shared. */
  slug: string
  label: string
  /** Internal documentation only - the banner deliberately never renders it. */
  blurb: string
  /** PROPOSAL only - tested against the name and note to suggest a tag. Never
      filters on its own; see proposeOccasions and matchesSeasonal. */
  match: RegExp
  when: DateSpec
  leadDays?: number
}

/** A def resolved against the calendar: this occurrence, and its live window. */
export interface SeasonalFilter extends SeasonalDef {
  /** The day itself. */
  date: Date
  /** First moment the chip is offered. */
  from: Date
  /** Last moment, end of the day itself. */
  until: Date
}

// Keep the regexes tight. A wide one is mostly harmless because the WINDOW
// already does the heavy lifting - a Christmas rule can't reach "Christmas in
// July" when the chip only exists from late November - but the events inside the
// window are the ones a reader will judge it on.
export const SEASONAL: SeasonalDef[] = [
  {
    slug: 'new-years-eve',
    label: "New Year's Eve",
    blurb: 'Countdowns, midnight toasts and ball drops.',
    match: /new year|\bnye\b|ball drop|midnight toast|countdown party/i,
    when: { kind: 'fixed', month: 12, day: 31 },
  },
  {
    slug: 'mlk-day',
    label: 'MLK Day',
    blurb: 'Marches, services and days of service for Dr. King.',
    match: /\bmlk\b|martin luther king|day of service/i,
    when: { kind: 'nth', month: 1, weekday: 1, nth: 3 },
  },
  {
    slug: 'black-history-month',
    label: 'Black History Month',
    blurb: 'Programs, panels and celebrations of Black history and culture.',
    match: /black history/i,
    when: { kind: 'monthRange', startMonth: 2, endMonth: 2 },
  },
  {
    slug: 'valentines',
    label: "Valentine's",
    blurb: 'Date nights, galentines and sweetheart specials.',
    match: /valentine|galentine|sweetheart/i,
    when: { kind: 'fixed', month: 2, day: 14 },
  },
  {
    slug: 'presidents-day',
    label: "Presidents' Day",
    blurb: 'Presidents Day events and long-weekend programming.',
    match: /presidents'? day|washington'?s birthday/i,
    when: { kind: 'nth', month: 2, weekday: 1, nth: 3 },
  },
  {
    slug: 'womens-history-month',
    label: "Women's History Month",
    blurb: "Panels, exhibits and events honoring women's history.",
    match: /women'?s history/i,
    when: { kind: 'monthRange', startMonth: 3, endMonth: 3 },
  },
  {
    slug: 'st-patricks',
    label: "St. Patrick's",
    blurb: 'Green beer, shamrocks and Irish music.',
    match: /st\.? ?patrick|shamrock|leprechaun|irish (?:music|night|session)|green beer/i,
    when: { kind: 'fixed', month: 3, day: 17 },
  },
  {
    slug: 'easter',
    label: 'Easter',
    blurb: 'Egg hunts, bunny photos and sunrise services.',
    match: /easter|egg hunt|eggstravaganza|bunny/i,
    when: { kind: 'easter' },
  },
  {
    slug: 'earth-day',
    label: 'Earth Day',
    blurb: 'Cleanups, plantings and river days.',
    match: /earth day|river clean|creek clean|cleanup|clean-?up|tree planting|litter/i,
    when: { kind: 'fixed', month: 4, day: 22 },
  },
  {
    slug: 'cinco-de-mayo',
    label: 'Cinco de Mayo',
    blurb: 'Cinco de Mayo parties and specials.',
    match: /cinco de mayo/i,
    when: { kind: 'fixed', month: 5, day: 5 },
  },
  {
    slug: 'mothers-day',
    label: "Mother's Day",
    blurb: 'Brunches, markets and make-something-for-mom sessions.',
    match: /mother'?s day|mothers day|for mom\b/i,
    when: { kind: 'nth', month: 5, weekday: 0, nth: 2 },
  },
  {
    slug: 'memorial-day',
    label: 'Memorial Day',
    blurb: 'Ceremonies, parades and the long weekend.',
    match: /memorial day/i,
    when: { kind: 'nth', month: 5, weekday: 1, nth: -1 },
  },
  {
    slug: 'aapi-heritage-month',
    label: 'AAPI Heritage Month',
    blurb: 'Celebrations of Asian American and Pacific Islander history and culture.',
    match: /asian american|pacific islander|\baapi\b/i,
    when: { kind: 'monthRange', startMonth: 5, endMonth: 5 },
  },
  {
    slug: 'fathers-day',
    label: "Father's Day",
    blurb: 'Cookouts, ballgames and make-something-for-dad sessions.',
    match: /father'?s day|fathers day|for dad\b/i,
    when: { kind: 'nth', month: 6, weekday: 0, nth: 3 },
  },
  {
    slug: 'juneteenth',
    label: 'Juneteenth',
    blurb: 'Juneteenth celebrations, cookouts and programs.',
    match: /juneteenth/i,
    when: { kind: 'fixed', month: 6, day: 19 },
  },
  {
    // No dedicated Lynchburg Pride festival turned up in a search on
    // 2026-08-19 (nearby options are Roanoke Pride in April and Richmond's
    // PrideFest in September - neither is Lynchburg's own). So this covers
    // June, national Pride Month, only. If a local fall event surfaces later,
    // add a second entry the way the file's own header describes rather than
    // widening this window.
    slug: 'pride',
    label: 'Pride',
    blurb: 'Pride Month celebrations, drag shows and LGBTQ+ community events.',
    match: /pride/i,
    when: { kind: 'monthRange', startMonth: 6, endMonth: 6 },
  },
  {
    slug: 'july-4',
    label: 'Fourth of July',
    blurb: 'Fireworks, parades and cookouts.',
    match: /fourth of july|4th of july|july 4|independence day|fireworks/i,
    when: { kind: 'fixed', month: 7, day: 4 },
  },
  {
    slug: 'labor-day',
    label: 'Labor Day',
    blurb: 'Labor Day weekend events.',
    match: /labor day/i,
    when: { kind: 'nth', month: 9, weekday: 1, nth: 1 },
  },
  {
    slug: 'hispanic-heritage-month',
    label: 'Hispanic Heritage Month',
    blurb: 'Celebrations of Hispanic and Latino history, culture and community.',
    match: /hispanic heritage|latino heritage|latina heritage/i,
    when: { kind: 'monthRange', startMonth: 9, startDay: 15, endMonth: 10, endDay: 15 },
  },
  {
    slug: 'halloween',
    label: 'Halloween',
    blurb: 'Trick-or-treating, costumes, haunts and pumpkins.',
    match: /halloween|trick.?or.?treat|trunk.?or.?treat|haunted|spooky|costume|pumpkin/i,
    when: { kind: 'fixed', month: 10, day: 31 },
  },
  {
    slug: 'veterans-day',
    label: 'Veterans Day',
    blurb: 'Ceremonies and events honoring veterans.',
    match: /veterans day|veteran'?s day/i,
    when: { kind: 'fixed', month: 11, day: 11 },
  },
  {
    slug: 'native-american-heritage-month',
    label: 'Native American Heritage Month',
    blurb: 'Programs honoring Native American and Indigenous history and culture.',
    match: /native american heritage|american indian heritage|indigenous peoples/i,
    when: { kind: 'monthRange', startMonth: 11, endMonth: 11 },
  },
  {
    slug: 'thanksgiving',
    label: 'Thanksgiving',
    blurb: 'Turkey trots, friendsgivings and community meals.',
    match: /thanksgiving|turkey trot|friendsgiving/i,
    when: { kind: 'nth', month: 11, weekday: 4, nth: 4 },
  },
  {
    slug: 'christmas',
    label: 'Christmas',
    blurb: 'Tree lightings, Santa visits, markets and caroling.',
    match: /christmas|santa|tree lighting|holiday market|caroling|nativity|menorah|hanukkah/i,
    when: { kind: 'fixed', month: 12, day: 25 },
  },
  {
    slug: 'first-friday',
    label: 'First Friday',
    blurb: 'The downtown art walk and the galleries open late.',
    match: /first friday|art walk|artwalk|gallery (?:walk|hop|crawl)/i,
    when: { kind: 'firstFridayMonthly' },
    leadDays: FIRST_FRIDAY_LEAD_DAYS,
  },
]

// ── National/food/awareness days ──────────────────────────────────────────
// A second, lower-stakes tier: "national days" (nationaldaycalendar.com) that
// people actually notice and businesses actually run specials for - Donut
// Day, Taco Day, Pi Day. There are hundreds of these and most are obscure
// (National Pothole Day is real), so this list is curation, not the full
// calendar - about 30 mainstream ones a Lynchburg reader would recognize.
// Deliberately absent: anything nobody local would plan around.
//
// Same rules as SEASONAL above apply - match PROPOSES, a person CONFIRMS, and
// the drawer only ever shows a chip once something upcoming actually matches
// it (see featuredOptions in App.tsx, which already gates on count > 0 for
// every entry from both tiers). The only difference is the lead time: 14
// days instead of 28, via NATIONAL_DAY_LEAD_DAYS on every entry below.
//
// A few of these are picked by an outside organizer each year rather than
// landing on a strict weekday formula (Record Store Day, Small Business
// Saturday, Teacher Appreciation Day's "first full week" rule) - those use
// `dates` with just the verified 2026 occurrence, same as the World Cup
// example in this file's header, and will need a new date added for 2027.
//
// Cinco de Mayo is deliberately NOT repeated here - it is already a major
// holiday above with the longer 28-day lead, and a second entry would double
// up the ?group=cinco-de-mayo slug.
export const NATIONAL_DAYS: SeasonalDef[] = [
  {
    slug: 'national-pizza-day',
    label: 'National Pizza Day',
    blurb: 'Pizza deals and slice specials.',
    match: /pizza/i,
    when: { kind: 'fixed', month: 2, day: 9 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'read-across-america-day',
    label: 'Read Across America Day',
    blurb: "Storytimes and readings tied to Dr. Seuss's birthday.",
    match: /read across america|dr\.? seuss/i,
    when: { kind: 'fixed', month: 3, day: 2 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'pi-day',
    label: 'Pi Day',
    blurb: 'Pie deals and math-club fun tied to 3.14.',
    match: /\bpi day\b/i,
    when: { kind: 'fixed', month: 3, day: 14 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'national-puppy-day',
    label: 'National Puppy Day',
    blurb: 'Puppy meetups and adoption events.',
    match: /national puppy day|puppy (?:social|party|adoption)/i,
    when: { kind: 'fixed', month: 3, day: 23 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    // WWF sets this as the last Saturday of March every year.
    slug: 'earth-hour',
    label: 'Earth Hour',
    blurb: 'Lights-out events marking the global hour for the planet.',
    match: /earth hour/i,
    when: { kind: 'nth', month: 3, weekday: 6, nth: -1 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    // National Arbor Day is fixed to the last Friday of April by federal
    // proclamation, not organizer choice, so a formula is safe here.
    slug: 'arbor-day',
    label: 'Arbor Day',
    blurb: 'Tree plantings and giveaways.',
    match: /arbor day|tree (?:planting|giveaway)/i,
    when: { kind: 'nth', month: 4, weekday: 5, nth: -1 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'national-beer-day',
    label: 'National Beer Day',
    blurb: 'Craft beer releases and taproom specials.',
    match: /national beer day|beer release/i,
    when: { kind: 'fixed', month: 4, day: 7 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    // Record Store Day picks its own date each year rather than following a
    // strict formula; verified 2026-08-19 against recordstoreday.com press
    // coverage. Needs a new date added for 2027.
    slug: 'record-store-day',
    label: 'Record Store Day',
    blurb: 'Exclusive releases and in-store events at local record shops.',
    match: /record store day/i,
    when: { kind: 'dates', dates: ['2026-04-18'] },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'free-comic-book-day',
    label: 'Free Comic Book Day',
    blurb: 'Free comics and signings at local comic shops.',
    match: /free comic book day|comic book day/i,
    when: { kind: 'nth', month: 5, weekday: 6, nth: 1 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'star-wars-day',
    label: 'Star Wars Day',
    blurb: 'May the Fourth screenings, trivia and costume meetups.',
    match: /star wars/i,
    when: { kind: 'fixed', month: 5, day: 4 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    // "Tuesday of the first full week of May" isn't a stable nth-weekday
    // formula (it depends on what day May 1st lands on); verified 2026-08-19
    // as May 5. Needs a new date added for 2027.
    slug: 'teacher-appreciation-day',
    label: 'Teacher Appreciation Day',
    blurb: 'Classroom treats and thank-you events for teachers.',
    match: /teacher appreciation/i,
    when: { kind: 'dates', dates: ['2026-05-05'] },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'national-nurses-day',
    label: 'National Nurses Day',
    blurb: 'Nurses Week programs and thank-you events.',
    match: /nurses? (?:day|week)|nurse appreciation/i,
    when: { kind: 'fixed', month: 5, day: 6 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'national-wine-day',
    label: 'National Wine Day',
    blurb: 'Wine tastings and vineyard specials.',
    match: /wine day|wine tasting|wine walk/i,
    when: { kind: 'fixed', month: 5, day: 25 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'bike-to-work-day',
    label: 'Bike to Work Day',
    blurb: 'Group commutes and bike-friendly events.',
    match: /bike to work/i,
    when: { kind: 'nth', month: 5, weekday: 5, nth: 3 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'national-donut-day',
    label: 'National Donut Day',
    blurb: 'Free donuts and specials at local shops.',
    match: /doughnut|\bdonuts?\b/i,
    when: { kind: 'nth', month: 6, weekday: 5, nth: 1 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'national-trails-day',
    label: 'National Trails Day',
    blurb: 'Group hikes and trail cleanups.',
    match: /national trails day|trail (?:hike|cleanup|day)/i,
    when: { kind: 'nth', month: 6, weekday: 6, nth: 1 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'national-ice-cream-day',
    label: 'National Ice Cream Day',
    blurb: 'Scoop specials and ice cream socials.',
    match: /ice cream/i,
    when: { kind: 'nth', month: 7, weekday: 0, nth: 3 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'national-hot-dog-day',
    label: 'National Hot Dog Day',
    blurb: 'Hot dog specials and cookouts.',
    match: /hot dog/i,
    when: { kind: 'nth', month: 7, weekday: 3, nth: 3 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'national-smores-day',
    label: "National S'mores Day",
    blurb: "S'mores bars and campfire nights.",
    match: /s'?mores/i,
    when: { kind: 'fixed', month: 8, day: 10 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'national-dog-day',
    label: 'National Dog Day',
    blurb: 'Pup-friendly meetups, adoption events and treats.',
    match: /national dog day|pup(?:py)? social|yappy hour|dog adoption/i,
    when: { kind: 'fixed', month: 8, day: 26 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'national-book-lovers-day',
    label: 'National Book Lovers Day',
    blurb: 'Book swaps, sales and club meetups.',
    match: /book lovers?|book swap|book sale/i,
    when: { kind: 'fixed', month: 8, day: 9 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'national-night-out',
    label: 'National Night Out',
    blurb: 'Neighborhood cookouts and community-police meetups.',
    match: /national night out/i,
    when: { kind: 'nth', month: 8, weekday: 2, nth: 1 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'national-coffee-day',
    label: 'National Coffee Day',
    blurb: 'Coffee deals and roastery specials.',
    match: /coffee/i,
    when: { kind: 'fixed', month: 9, day: 29 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'national-pancake-day',
    label: 'National Pancake Day',
    blurb: 'Pancake breakfasts and stacks-for-a-cause events.',
    match: /pancake/i,
    when: { kind: 'fixed', month: 9, day: 26 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'national-public-lands-day',
    label: 'National Public Lands Day',
    blurb: 'Park cleanups and fee-free days on public land.',
    match: /public lands day/i,
    when: { kind: 'nth', month: 9, weekday: 6, nth: 4 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'national-taco-day',
    label: 'National Taco Day',
    blurb: 'Taco deals and specials around town.',
    match: /taco/i,
    when: { kind: 'fixed', month: 10, day: 4 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'national-cat-day',
    label: 'National Cat Day',
    blurb: 'Cat adoption events and cat-café meetups.',
    match: /national cat day|cat adoption|cat caf[eé]/i,
    when: { kind: 'fixed', month: 10, day: 29 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    // "Saturday following Thanksgiving" isn't always the same nth-Saturday of
    // November (it depends how many Saturdays land before it); verified
    // 2026-08-19 as Nov 28. Needs a new date added for 2027.
    slug: 'small-business-saturday',
    label: 'Small Business Saturday',
    blurb: 'Shop-small specials at local businesses.',
    match: /small business saturday|shop small/i,
    when: { kind: 'dates', dates: ['2026-11-28'] },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
  {
    slug: 'world-kindness-day',
    label: 'World Kindness Day',
    blurb: 'Random-acts-of-kindness drives and community give-back events.',
    match: /kindness day|random acts? of kindness/i,
    when: { kind: 'fixed', month: 11, day: 13 },
    leadDays: NATIONAL_DAY_LEAD_DAYS,
  },
]

// Every def across both tiers, in one place, for anything that needs to
// search the full set rather than just SEASONAL - seasonalFilters() below,
// and occasionLabel() over in lib/occasions.ts.
export const ALL_OCCASIONS: SeasonalDef[] = [...SEASONAL, ...NATIONAL_DAYS]

/** A curated row's live window. Either end may be null, meaning no bound there. */
export interface LiveWindow {
  show_from?: string | null
  show_until?: string | null
}

/**
 * Is a windowed row live at `now`? The STORED counterpart of the computed
 * windows above, kept beside them so every "is this filter on right now"
 * decision lives in one file.
 *
 * A null show_from means live from the moment it existed, which is exactly why
 * this can't be left to a `lte` in the query: comparing against NULL drops the
 * row rather than keeping it. Both bounds are inclusive.
 */
export function isLiveNow(row: LiveWindow, now: Date): boolean {
  const t = now.getTime()
  if (row.show_from && new Date(row.show_from).getTime() > t) return false
  if (row.show_until && new Date(row.show_until).getTime() < t) return false
  return true
}

/** Anonymous Gregorian computus. Returns Easter Sunday in local time. */
function easterSunday(year: number): Date {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31) // 3 = March, 4 = April
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(year, month - 1, day)
}

/** nth is 1-4, or -1 for the last one in the month. weekday is 0=Sun..6=Sat. */
function nthWeekday(year: number, month: number, weekday: number, nth: number): Date {
  if (nth < 0) {
    // Day 0 of the next month is the last day of this one.
    const last = new Date(year, month, 0)
    const back = (last.getDay() - weekday + 7) % 7
    return new Date(year, month - 1, last.getDate() - back)
  }
  const first = new Date(year, month - 1, 1)
  const forward = (weekday - first.getDay() + 7) % 7
  return new Date(year, month - 1, 1 + forward + (nth - 1) * 7)
}

/** Every occurrence of a def inside one calendar year. For `monthRange` this
    is the START of the observance - see occurrenceUntil for the end. */
function occurrences(def: SeasonalDef, year: number): Date[] {
  switch (def.when.kind) {
    case 'fixed':
      return [new Date(year, def.when.month - 1, def.when.day)]
    case 'nth':
      return [nthWeekday(year, def.when.month, def.when.weekday, def.when.nth)]
    case 'easter':
      return [easterSunday(year)]
    case 'firstFridayMonthly':
      return Array.from({ length: 12 }, (_, m) => nthWeekday(year, m + 1, 5, 1))
    case 'dates':
      return def.when.dates
        .map((d) => {
          const [y, m, day] = d.split('-').map(Number)
          return new Date(y, (m ?? 1) - 1, day ?? 1)
        })
        .filter((d) => d.getFullYear() === year)
    case 'monthRange':
      return [new Date(year, def.when.startMonth - 1, def.when.startDay ?? 1)]
  }
}

/**
 * The last day a def's window should stay live. Every kind except
 * `monthRange` is a single day, so this is just that day; `monthRange` runs
 * through its own end instead, independent of when the def's own `date`
 * (its start) falls. `new Date(year, month, 0)` is the last day of `month`
 * (1-indexed) - day 0 of the next month rolls back one day, same trick
 * nthWeekday's `nth: -1` branch uses above.
 */
function occurrenceUntil(def: SeasonalDef, year: number, date: Date): Date {
  if (def.when.kind !== 'monthRange') return date
  const { endMonth, endDay } = def.when
  return new Date(year, endMonth - 1, endDay ?? new Date(year, endMonth, 0).getDate())
}

/**
 * The filters whose window contains `today`, soonest day first.
 *
 * Three years are scanned because a window can straddle New Year: MLK falls in
 * mid-January, so its chip is already up in the previous December.
 */
export function seasonalFilters(today: Date): SeasonalFilter[] {
  const now = today.getTime()
  const year = today.getFullYear()
  const out: SeasonalFilter[] = []

  for (const def of ALL_OCCASIONS) {
    const lead = def.leadDays ?? HOLIDAY_LEAD_DAYS
    for (const y of [year - 1, year, year + 1]) {
      for (const date of occurrences(def, y)) {
        const from = startOfDay(addDays(date, -lead))
        const until = endOfDay(occurrenceUntil(def, y, date))
        if (now >= from.getTime() && now <= until.getTime()) {
          out.push({ ...def, date, from, until })
        }
      }
    }
  }

  return out.sort((a, b) => a.date.getTime() - b.date.getTime())
}

/** The active filter for a slug, or null when its day has passed. */
export function seasonalBySlug(slug: string | null, today: Date): SeasonalFilter | null {
  if (!slug) return null
  return seasonalFilters(today).find((s) => s.slug === slug) ?? null
}

/** lyh_events.occasions. Present on every row; not yet in the EventRow type. */
type TaggedEvent = EventRow & { occasions?: string[] | null }

/**
 * Membership is the CONFIRMED tag, never the keyword rule. An untagged event
 * belongs to nothing, so a wrong filter can't appear - only a missing one, which
 * someone can fix by confirming a proposal.
 */
export function matchesSeasonal(e: EventRow, s: SeasonalDef): boolean {
  const tags = (e as TaggedEvent).occasions
  return Array.isArray(tags) && tags.includes(s.slug)
}

/**
 * The occasions worth OFFERING for an event, for a person to accept or reject.
 * Nothing here is applied on its own.
 *
 * Asked against the event's own date rather than today, so the only candidates
 * are occasions whose run-up the event actually falls inside. That alone throws
 * out the "Christmas in July" class of mistake before a person ever sees it, and
 * it means an event can be tagged the day it's approved, months ahead.
 */
export function proposeOccasions(e: EventRow): string[] {
  const when = new Date(e.start_time)
  if (Number.isNaN(when.getTime())) return []
  const haystack = `${e.name} ${e.note ?? ''}`
  return seasonalFilters(when)
    .filter((s) => s.match.test(haystack))
    .map((s) => s.slug)
}

/**
 * Dressed as a saved group so the banner, the ?group= URL and Clear All treat it
 * like any other. `hosts` is empty because a seasonal filter is a name rule, and
 * the banner's Includes line correctly stays off for it.
 */
export function seasonalAsGroup(s: SeasonalFilter): FilterGroup {
  return {
    slug: s.slug,
    label: s.label,
    blurb: s.blurb,
    hosts: [],
    name_match: s.match.source,
    days: null,
    source_url: null,
  }
}
